import { LoadingButton } from '@mui/lab';
import { Button, FormControlLabel, Switch, Typography } from '@mui/material';
import React, { ReactNode, useCallback, useMemo } from 'react';
import { SIZES } from '../../../../constants/constants';
import { useI18n } from '../../../../i18n/I18n';
import { H, V } from '../../../../Layout';
import type { WorkEntriesDatagridProps, WorkEntryRow } from '../../../types';
import { applyWorkEntryOrder } from '../../utils';

type FooterFilterMode = NonNullable<WorkEntriesDatagridProps['footerFilterMode']>;

interface WorkEntriesFooterProps<TSource> {
    rows: ReadonlyArray<WorkEntryRow<TSource>>;
    orderedIds: ReadonlyArray<string>;
    selectedIds: ReadonlySet<string>;
    showFooterFilteredOnly: boolean;
    onToggleFooterFilter: (value: boolean) => void;
    footerFilterMode: FooterFilterMode;
    footerFilterLabel?: ReactNode;
    enableSelection: boolean;
    onBulkAction?: (selectedIds: string[], selectedRows: WorkEntryRow<TSource>[]) => void;
    bulkActionLoading?: boolean;
    bulkActionLabel?: ReactNode;
    showBackButton: boolean;
    backLabel?: ReactNode;
    onBack?: () => void;
    stickyFooter: boolean;
    compactHorizontalPadding?: boolean;
    footerAccessory?: ReactNode;
}

export default function WorkEntriesFooter<TSource>({
    rows,
    orderedIds,
    selectedIds,
    showFooterFilteredOnly,
    onToggleFooterFilter,
    footerFilterMode,
    footerFilterLabel,
    enableSelection,
    onBulkAction,
    bulkActionLoading = false,
    bulkActionLabel,
    showBackButton,
    backLabel,
    onBack,
    stickyFooter,
    compactHorizontalPadding = false,
    footerAccessory
}: WorkEntriesFooterProps<TSource>) {
    const t = useI18n();

    const resolvedFooterFilterLabel = useMemo(
        () => footerFilterLabel ?? t('workEntries.filter.show.selected'),
        [footerFilterLabel, t]
    );

    const showFooterFilterToggle = footerFilterMode === 'selected' && enableSelection;
    const resolvedBulkActionLabel = bulkActionLabel ?? t('common.save');
    const resolvedBackLabel = backLabel ?? t('dialog.back');

    const orderedRows = useMemo(() => applyWorkEntryOrder(rows, orderedIds), [rows, orderedIds]);
    const selectedRowIds = useMemo(
        () => orderedRows.filter((row) => selectedIds.has(row.id)).map((row) => row.id),
        [orderedRows, selectedIds]
    );
    const selectedRows = useMemo(
        () => orderedRows.filter((row) => selectedIds.has(row.id)),
        [orderedRows, selectedIds]
    );

    const handleBack = useCallback(() => {
        if (onBack) {
            onBack();
            return;
        }
        window.history.back();
    }, [onBack]);

    const handleBulkAction = useCallback(() => {
        onBulkAction?.(selectedRowIds, selectedRows);
    }, [onBulkAction, selectedRowIds, selectedRows]);

    return (
        <V
            fill
            sx={{
                position: stickyFooter ? 'sticky' : 'relative',
                bottom: stickyFooter ? 0 : 'auto',
                mt: 1,
                bgcolor: 'background.paper',
                borderTop: '1px solid',
                borderTopColor: 'divider'
            }}
        >
            {footerAccessory ? (
                <H
                    fill
                    sx={{
                        py: 1,
                        px: compactHorizontalPadding ? 0 : 1,
                        justifyContent: 'flex-end',
                        alignItems: 'center'
                    }}
                >
                    {footerAccessory}
                </H>
            ) : null}
            <H
                fill
                sx={{
                    py: 1.5,
                    px: compactHorizontalPadding ? 0 : 1,
                    alignItems: 'center',
                    justifyContent: showBackButton ? 'space-between' : 'flex-end'
                }}
            >
                {showBackButton ? (
                    <Button
                        variant="text"
                        size={SIZES.small}
                        onClick={handleBack}
                        sx={{ flexShrink: 0, textTransform: 'none' }}
                    >
                        {resolvedBackLabel}
                    </Button>
                ) : null}

                <H
                    sx={{
                        alignItems: 'center',
                        width: 'auto',
                        gap: 1,
                        justifyContent: 'flex-end'
                    }}
                >
                    {showFooterFilterToggle ? (
                        <FormControlLabel
                            sx={{ mr: 0 }}
                            control={
                                <Switch
                                    checked={showFooterFilteredOnly}
                                    onChange={(_, value) => onToggleFooterFilter(value)}
                                    size={SIZES.small}
                                />
                            }
                            label={
                                <Typography variant="body2" noWrap>
                                    {resolvedFooterFilterLabel}
                                </Typography>
                            }
                        />
                    ) : null}
                    {enableSelection && onBulkAction ? (
                        <LoadingButton
                            variant="contained"
                            size={SIZES.small}
                            onClick={handleBulkAction}
                            loading={bulkActionLoading}
                            disabled={selectedRowIds.length === 0}
                            sx={{ textTransform: 'none' }}
                        >
                            {resolvedBulkActionLabel}
                        </LoadingButton>
                    ) : null}
                </H>
            </H>
        </V>
    );
}
